import * as Action from '../actions/AppAction';
import { UserData } from '../userdata/types';
import { PagesState, initPagesState } from './pages';
import {
  IntegratedDrawingState, dejsonizeIntegratedDrawingData,
  initIntegratedDrawingState, collectPictureIDsFromJSONReadyIntegratedDrawingData,
} from './integratedDrawing';
import { PreferenceState, initPreferenceState, initContentFeatureFlags } from './preference';
import { ViewState, initViewState } from './view';
import { SpeechState, initSpeechState, TTSStatus } from '../tts/reducers/speech';
import { UIModeState, initUIModeState } from './uimode';
import { Publication } from '../types/epub';
import { DrawingToolState, initDrawingToolState } from '../drawing/reducers/drawingTool';
import autoSaving, { AutoSavingState, isAutoSavingRunning } from './autoSaving';
import { ContentStorageState } from './contentStorage';
import { MarkerInfo } from '../marker';
import { createUserData } from '../userdata/save';
import AutoSavingAction from '../actions/AutoSavingAction';
import {
  PictureStorageState,
  initPictureStorage,
  initPictureStorageItems,
  preparePictureItemsForSaving,
  finalizePictureItemsForSaving,
  prunePictureItems,
} from '../drawing/reducers/pictureStorage';
import {
  createIsImageDemanded,
  createIsPictureToBeSaved,
  createIsPictureUsed,
} from '../helpers/integratedDrawings';
import { SelectionState, initSelectionState } from './selection';
import { MarkerToolState, initMarkerToolState } from '../marker/reducers/marker';
import { BookmarksState } from '../bookmark/reducers/bookmark';
import { ThumbnailInfo } from '../types/thumbnail';
import { buildPageBlocks } from '../utils/epub';
import { UIState, initUIState, CurrentTool } from './ui/ui';
import { EntireComposerState, initEntireComposerState } from '../clipbook/reducers/composer';

export interface AppStates {
  publication: Publication | undefined;
  installedComponents: ReadonlyArray<string>;
  pages: PagesState;
  preference: PreferenceState;
  integratedDrawing: IntegratedDrawingState;
  drawingTool: DrawingToolState;
  view: ViewState;
  speech: SpeechState;
  uimode: UIModeState;
  ui: UIState;
  pictureStorage: PictureStorageState<HTMLCanvasElement>;
  autoSaving: AutoSavingState;
  contentStorage: ContentStorageState;
  selectionState: SelectionState;
  markers: MarkerInfo[];
  bookmarks: BookmarksState;
  savingWorkspace: UserData | null;
  markerTool: MarkerToolState;
  thumbnail: ThumbnailInfo;
  composer: EntireComposerState;
  composerMode: boolean;
  legacyUI: boolean;
  developMode: boolean;
}

export function prepareWorkspaceSaving(state: AppStates): AppStates {
  if (state.publication === undefined || state.savingWorkspace !== null) {
    throw new Error('Illegal state.');
  }

  const savingWorkspace = createUserData(state.publication.id, {
    pages: state.pages,
    preference: state.preference,
    drawingTool: state.drawingTool,
    integratedDrawing: state.integratedDrawing,
    speech: state.speech,
    contentStorage: state.contentStorage,
    markers: state.markers,
    bookmarks: state.bookmarks,
    view: { ...initViewState(), popupRects: state.view.popupRects },
    ui: state.ui,
  });

  return {
    ...state,
    savingWorkspace,
    pictureStorage: preparePictureItemsForSaving(
      state.pictureStorage,
      createIsPictureToBeSaved(savingWorkspace.integratedDrawings),
    ),
  };
}

export function startWorkspaceSaving(state: AppStates): AppStates {
  if (state.savingWorkspace === null) {
    throw new Error('Illegal state.');
  }

  return {
    ...state,
    pictureStorage: finalizePictureItemsForSaving(state.pictureStorage),
  };
}

export function userDataToAppStates(data: UserData, state: AppStates): AppStates {
  const publication = state.publication;
  if (publication === undefined) {
    throw new Error('Null publication.');
  }

  const integratedDrawing = dejsonizeIntegratedDrawingData(data.integratedDrawings);
  const pictureIDs = collectPictureIDsFromJSONReadyIntegratedDrawingData(data.integratedDrawings);

  return {
    integratedDrawing,
    publication: undefined,
    installedComponents: [],
    pages: data.pages,
    preference: data.preference,
    drawingTool: { ...initDrawingToolState(), ...data.drawingTool },
    view: { ...initViewState(), popupRects: data.popupRects },
    speech: { ...data.speech, status: TTSStatus.Idle },
    ui: { ...initUIState(), ...data.ui, currentTool: CurrentTool.Touch },
    uimode: { ...initUIModeState() },
    pictureStorage: initPictureStorageItems(
      initPictureStorage(),
      createIsImageDemanded(integratedDrawing, data.pages, publication),
      pictureIDs,
    ),
    autoSaving: AutoSavingState.Disabled,
    contentStorage: data.contentStorage,
    selectionState: initSelectionState(),
    markers: data.markers,
    bookmarks: data.bookmarks,
    savingWorkspace: null,
    markerTool: initMarkerToolState(),
    thumbnail: { pageBlocks: [] },
    composer: state.composer,
    composerMode: state.composerMode,
    legacyUI: state.legacyUI,
    developMode: state.developMode,
  };
}

export const initAppStates = (): AppStates => {
  return {
    publication: undefined,
    installedComponents: [],
    integratedDrawing: initIntegratedDrawingState(),
    pages: initPagesState(),
    preference: initPreferenceState(),
    drawingTool: initDrawingToolState(),
    view: initViewState(),
    speech: initSpeechState(),
    uimode: initUIModeState(),
    ui: initUIState(),
    pictureStorage: initPictureStorage(),
    autoSaving: AutoSavingState.Disabled,
    contentStorage: {},
    selectionState: initSelectionState(),
    markers: [],
    bookmarks: [],
    savingWorkspace: null,
    markerTool: initMarkerToolState(),
    thumbnail: { pageBlocks: [] },
    composer: initEntireComposerState(),
    composerMode: false,
    legacyUI: false,
    developMode: false,
  };
};

// The target field of PictureStorageAction.
export enum AppPictureStorage {
  Drawing,
}

export const appReducer = (state: AppStates, action: Action.AppAction): AppStates => {
  switch (action.type) {
    case Action.APP_REPLACE_STATES:
      // ロード後のWorkspace読み込みでの利用を想定しているため、publication等は上書きしない
      // FIXME : Action名と挙動が一致していない

      if (state.publication === undefined) {
        throw new Error('Empty publication.');
      }

      return {
        ...action.data,
        publication: state.publication,
        installedComponents: state.installedComponents,
        uimode: state.uimode,
        autoSaving: state.autoSaving,
        savingWorkspace: state.savingWorkspace,
        thumbnail: { pageBlocks: buildPageBlocks(state.publication.renditions[0]) },
      };

    case Action.APP_SET_PUBLICATION:
      if (state.publication !== undefined) {
        throw new Error('Currently replacing publication is not implemented.');
      }
      return {
        ...state,
        publication: action.publication,
        installedComponents: action.installedComponents,
        preference:
          initContentFeatureFlags(state.preference, action.publication.featureDefinitions),
        thumbnail: { pageBlocks: buildPageBlocks(action.publication.renditions[0]) },
      };

    case Action.APP_START_SAVING_PROCESS:
      return prepareWorkspaceSaving(state);

    case Action.APP_FINISH_SAVING_PROCESS:
      return {
        ...state,

        // Item which is saved on the disk but no longer used may exist.
        pictureStorage: prunePictureItems(
          state.pictureStorage,
          createIsPictureUsed(state.integratedDrawing),
        ),

        autoSaving: (
          isAutoSavingRunning(state.autoSaving)
            ? autoSaving(state.autoSaving, AutoSavingAction.finishProcess())
            : state.autoSaving
        ),
        savingWorkspace: null,
      };

    case Action.APP_SET_COMPOSER_MODE:
      return {
        ...state,
        composerMode: action.composerMode,
      };

    case Action.APP_SET_LEGACY_UI:
      return {
        ...state,
        legacyUI: !state.legacyUI,
      };

    case Action.APP_SET_DEVELOP_MODE:
      return {
        ...state,
        developMode: action.developMode,
      };
  }
};
